import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import {dbase} from "../../../firebase/index";
import {byPropKey} from "../../utils";
import ScrollToTop from 'react-scroll-up';
import RecordingCard from "./recordingcard/RecordingCard";
import './styles.css';
import Throbber from "../../miscellaneous/Throbber";
import {getUnitID} from "../utils";
import PaginationController from "./search/PaginationController";
import {search} from "./search/utils";
import ReactToolTip from 'react-tooltip';
import {BOOTSTRAP_MIN_SM} from "../../../constants/bootstrapResponsive";
import MediaQuery from "react-responsive";

/** The search ID for search tooltip */
const TOOLTIP_SEARCH_ID = "searchTip";

/**
 * Initial/ fallback state
 */
const INITIAL_STATE = {
    recordings: [],
    searchResults: [],
    searchPageOfItems: [],
    searchString: '',
    isTypingInSearchBar: true,
    searchTimeout: null,
    error: null
};

/**
 * Recordings (infinite scrolling) tab wrapper
 */
class RecordingsTab extends Component {
    constructor(props) {
        super(props);

        this.state = {
            ...INITIAL_STATE
        };
    }

    componentDidMount() {
        if (this.props.selectedUnit) {
            this.loadAllRecordings(this.props.selectedUnit);
        }
    }

    componentWillReceiveProps(nextProps) {
        const isSelectedUnitChanged = this.props.selectedUnit &&
            getUnitID(this.props.selectedUnit) !== getUnitID(nextProps.selectedUnit);
        if (isSelectedUnitChanged) {
            // Reload all recordings for the new unit if the unit is changed.
            this.setState(byPropKey('recordings', INITIAL_STATE.recordings));
            this.loadAllRecordings(nextProps.selectedUnit, isSelectedUnitChanged);
        }
    }

    /**
     * Loads all recordings for the {@param selectedUnit} onto
     * state variables, {@code recordings} and {@code searchResults}
     *
     * @param unitChanged should be supplied and {@code true}
     * if this is called during a change of units.
     */
    loadAllRecordings = (selectedUnit, unitChanged) => {
        // 1. Reset existing state and let the throbber display while loading
        if (unitChanged) {
            this.setState(byPropKey('searchString', INITIAL_STATE.searchString));
            this.setState(byPropKey('searchResults', INITIAL_STATE.recordings));
        } else {
            this.clearSearchBar();
        }
        this.setState(byPropKey('isTypingInSearchBar', INITIAL_STATE.isTypingInSearchBar));
        // 2. Attempt to obtain all recordings of a unitID once into a low memory json
        dbase.getRecordingsOnce(getUnitID(selectedUnit))
            .then(snapshot => {
                const recordingsSnapshot = snapshot.val();
                // 3. Sort the obtained values by key and reverse them (latest first by default)
                const arrayOfKeys = Object.keys(recordingsSnapshot).sort().reverse();
                let results = [];
                // 4. Map the obtained json into our Recording schema to be displayed to the endUser.
                //  Note that we require the prop {@code recordingID} so we can map later, passing a unique key
                for (let i = 0; i < arrayOfKeys.length; i++) {
                    results.push({...recordingsSnapshot[arrayOfKeys[i]], recordingID: arrayOfKeys[i]});
                }
                // 5. Update the recordings set with the results. TREAT {@code results} AS IMMUTABLE
                this.setState(byPropKey('recordings', results));
                // 6. Queue a timeout to load ALL recordings onto the search parameter
                //  The behaviour specification is such that we want all recordings to be displayed on load.
                setTimeout(() => {
                    this.setState(byPropKey('searchResults', results));
                    this.setState(byPropKey('isTypingInSearchBar', false));
                }, 500);
            })
            .catch(error => {
                this.setState(byPropKey('error', error));
            });
    };

    /**
     * Clears the search bar and resets the filter to the state's {@code recordings}
     * @param event
     */
    clearSearchBar = (event) => {
        this.setState(byPropKey('searchString', INITIAL_STATE.searchString));
        this.setState(byPropKey('searchResults', this.state.recordings));
        if (event) {
            event.preventDefault();
        }
    };

    /**
     * Causes the sub{@param component} to be focused when the Component is mounted
     */
    focusInput = (component) => {
        if (component) {
            ReactDOM.findDOMNode(component).focus();
        }
    };

    /**
     * Triggered on a page change, to be handled by the component {@code PaginationController}
     * @param searchPageOfItems
     */
    onChangePage = (searchPageOfItems) => {
        this.setState(byPropKey('searchPageOfItems', searchPageOfItems));
    };

    /**
     * Triggered on a manual call to search the recordings.
     * @param event
     */
    onSearch = (event) => {
        this.setState(byPropKey('searchResults', search(null, this.state.searchString, null, this.state.recordings)));
        this.setState(byPropKey('isTypingInSearchBar', false));
        if (event) {
            event.preventDefault();
        }
    };

    /**
     * Is called every time a the search input box text is changed
     * @param event
     */
    onSearchInputChange = (event) => {
        this.setState(byPropKey('searchString', event.target.value));
        this.setState(byPropKey('isTypingInSearchBar', true));
        this.setState(byPropKey('searchResults', []));
        if (this.state.searchTimeout) {
            clearTimeout(this.state.searchTimeout);
        }
        const newTimeout = setTimeout(() => {
            this.onSearch();
        }, 500);
        this.setState(byPropKey('searchTimeout', newTimeout));
    };

    render() {
        const disableClear = this.state.searchString === INITIAL_STATE.searchString;
        return (
            <div>
                <div className="text-center">
                    <form className="form-inline" onSubmit={this.onSearch}>
                        <input
                            ref={this.focusInput}
                            className="mt-1 form-control"
                            type="search"
                            value={this.state.searchString}
                            onChange={this.onSearchInputChange}
                            placeholder="Search..."
                        />
                        <div className="mt-1">
                            <div
                                data-tip
                                data-for={TOOLTIP_SEARCH_ID}
                                className="ml-lg-2 ml-md-2 ml-sm-2 btn btn-primary"
                            >
                                <i className="fas fa-search"/> Search
                            </div>
                            <MediaQuery minWidth={BOOTSTRAP_MIN_SM}>
                                <ReactToolTip id={TOOLTIP_SEARCH_ID} place="bottom" effect="solid">
                                    <div className="text-left">
                                        <b>Search</b>
                                        <p>You can search through the titles, dates, tags, impact, privacy, or unread status.</p>
                                        <p>For example, searching: </p>
                                        <ul>
                                            <li><b><i>med</i></b> or <b><i>medium</i></b> shows you all recordings uploaded after a high impact.</li>
                                            <li><b><i>unread</i></b> shows you all unread recordings.</li>
                                            <li><b><i>private</i></b> shows you all private recordings.</li>
                                            <li><b><i>2017</i></b> shows you all recordings in 2017.</li>
                                            <li><b><i>hello there</i></b> shows you all recordings with <b>hello</b> and <b>there</b> in the title or tags.</li>
                                            <li><b><i>high unread monday</i></b> shows you unread recordings on a monday with a high impact.</li>
                                        </ul>
                                    </div>
                                </ReactToolTip>
                            </MediaQuery>
                            <div
                                className={"ml-2 btn btn-outline-dark" + (disableClear ? " disabled" : "")}
                                onClick={this.clearSearchBar}
                            >
                                Clear
                            </div>
                        </div>
                    </form>
                </div>
                <div>
                    {
                        this.state.isTypingInSearchBar &&
                        <div className="text-center">
                            <Throbber/>
                        </div>
                    }
                    <div className="row">
                        {
                            this.state.searchPageOfItems.map(item =>
                                <RecordingCard
                                    key={item.recordingID}
                                    recordingID={item.recordingID}
                                    recordingData={item}
                                    selectedUnit={this.props.selectedUnit}
                                />
                            )
                        }
                    </div>
                    <div className="row">
                        <PaginationController isTypingInSearchBar={this.state.isTypingInSearchBar} items={this.state.searchResults} onChangePage={this.onChangePage} />
                    </div>
                </div>
                {
                    this.state.error &&
                    <p className="error">{this.state.error.message}</p>
                }
                <ScrollToTop showUnder={200}>
                    <button
                        className="btn btn-secondary"
                        style={{opacity: 0.5}}
                    >
                        <i className="fas fa-caret-up"/>
                    </button>
                </ScrollToTop>
            </div>
        );
    }
}

export default RecordingsTab;



// WEBPACK FOOTER //
// ./src/components/unit/recordings/RecordingsTab.jsx